import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import GoogleSignInButton from './GoogleSignInButton';
import SeoHead from './SeoHead';
import { pageMeta } from '../seoUtils';
import { PasswordInput } from './index';

export default function LoginPage({ onNavigate }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { login, verifyTwoFactorLogin, reactivateAccount, loading, error, clearError, user } =
    useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [reactivationToken, setReactivationToken] = useState(null);
  const [reactivating, setReactivating] = useState(false);

  // 2FA challenge state
  const [twoFactorPending, setTwoFactorPending] = useState(false);
  const [twoFactorToken, setTwoFactorToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);
  const stateFrom = location.state?.from;
  const redirectPath = typeof stateFrom === 'string' && stateFrom.startsWith('/') ? stateFrom : '/';

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      onNavigate(redirectPath);
    }
  }, [user, onNavigate, redirectPath]);

  useEffect(() => {
    clearError();
  }, [clearError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldError('');
    setReactivationToken(null);

    if (!email.trim() || !password) {
      setFieldError('Please enter your email and password.');
      return;
    }

    const result = await login({ email: email.trim(), password });
    if (!result.ok) {
      if (result.code === 'account_deactivated' && result.reactivationToken) {
        setReactivationToken(result.reactivationToken);
      }
      return;
    } // error set by context

    if (result.requiresTwoFactor) {
      setTwoFactorToken(result.twoFactorToken);
      setTwoFactorPending(true);
      return;
    }

    onNavigate(redirectPath);
  };

  const handleReactivate = async () => {
    if (!reactivationToken) {
      return;
    }
    setReactivating(true);
    setFieldError('');
    const result = await reactivateAccount(reactivationToken);
    setReactivating(false);
    if (!result.ok) {
      setFieldError(result.error || 'Unable to reactivate account.');
      return;
    }

    setReactivationToken(null);
    const loginResult = await login({ email: email.trim(), password });
    if (loginResult.ok) {
      onNavigate(redirectPath);
    }
  };

  const handleTwoFactorSubmit = async (e) => {
    e.preventDefault();
    setFieldError('');

    const code = totpCode.replace(/\s+/g, '');
    if (!code || code.length !== 6) {
      setFieldError('Please enter the 6-digit code from your authenticator app.');
      return;
    }

    const result = await verifyTwoFactorLogin({ code, twoFactorToken, rememberDevice });
    if (result.ok) {
      onNavigate(redirectPath);
    }
  };

  const handleGoogleSuccess = () => {
    onNavigate(redirectPath);
  };

  const handleGoogleError = (errorMsg) => {
    setFieldError(errorMsg);
  };

  const isSuspended = (fieldError || error || '').toLowerCase().includes('suspended');
  const displayError = fieldError || error;

  if (twoFactorPending) {
    return (
      <main className="auth-page">
        <div className="auth-card">
          <div className="auth-card__header">
            <h1 className="auth-card__title">Two-factor verification</h1>
            <p className="auth-card__subtitle">
              Enter the 6-digit code from your authenticator app
            </p>
          </div>

          <form className="auth-form" onSubmit={handleTwoFactorSubmit} noValidate>
            {displayError && (
              <div className="auth-form__error" role="alert">
                {displayError}
              </div>
            )}

            <div className="auth-form__field">
              <label htmlFor="totp-code" className="auth-form__label">
                Authentication code
              </label>
              <input
                id="totp-code"
                type="text"
                inputMode="numeric"
                pattern="[0-9 ]*"
                maxLength={7}
                className="auth-form__input auth-form__input--otp"
                placeholder="000 000"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                autoComplete="one-time-code"
                autoFocus
                required
              />
            </div>

            <label className="auth-form__checkbox-row">
              <input
                type="checkbox"
                checked={rememberDevice}
                onChange={(e) => setRememberDevice(e.target.checked)}
              />
              Trust this device for 30 days
            </label>

            <button type="submit" className="auth-form__submit" disabled={loading}>
              {loading ? 'Verifying…' : 'Verify and log in'}
            </button>
          </form>

          <p className="auth-card__footer">
            <button
              type="button"
              className="auth-card__link"
              onClick={() => {
                setTwoFactorPending(false);
                setTotpCode('');
                setRememberDevice(false);
                setFieldError('');
              }}
            >
              ← Back to login
            </button>
          </p>
        </div>
      </main>
    );
  }

  const seo = pageMeta({
    title: 'Log In',
    description:
      'Log in to your Naija Nest Properties account to manage your listings and saved properties.',
    path: '/login',
    noIndex: true,
  });

  return (
    <main className="auth-page">
      <SeoHead
        title={seo.fullTitle}
        description={seo.desc}
        url={seo.url}
        image={seo.img}
        noIndex={seo.noIndex}
      />
      <div className="auth-card">
        <div className="auth-card__header">
          <h1 className="auth-card__title">Welcome back</h1>
          <p className="auth-card__subtitle">Log in to your NaijaNest account</p>
        </div>

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          {displayError && !isSuspended && !reactivationToken && (
            <div className="auth-form__error" role="alert">
              {displayError}
            </div>
          )}
          {reactivationToken && (
            <div className="auth-form__error" role="alert">
              <strong>Account deactivated.</strong> {displayError}
              <div style={{ marginTop: '0.75rem' }}>
                <button
                  type="button"
                  className="auth-form__submit"
                  onClick={handleReactivate}
                  disabled={reactivating}
                >
                  {reactivating ? 'Reactivating…' : 'Reactivate my account'}
                </button>
              </div>
            </div>
          )}
          {isSuspended && (
            <div className="auth-form__error auth-form__error--suspended" role="alert">
              <strong>Account suspended.</strong> Your account has been suspended. If you believe
              this is an error, please contact{' '}
              <a href="mailto:hello@naijanestproperties.com">hello@naijanestproperties.com</a>.
            </div>
          )}

          <div className="auth-form__field">
            <label htmlFor="login-email" className="auth-form__label">
              Email address
            </label>
            <input
              id="login-email"
              type="email"
              className="auth-form__input"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </div>

          <div className="auth-form__field">
            <label htmlFor="login-password" className="auth-form__label">
              Password
            </label>
            <PasswordInput
              id="login-password"
              className="auth-form__input"
              placeholder="Your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
            <button
              type="button"
              className="auth-form__forgot-link"
              onClick={() => onNavigate('/forgot-password')}
            >
              Forgot password?
            </button>
          </div>

          <button type="submit" className="auth-form__submit" disabled={loading}>
            {loading ? 'Logging in…' : 'Log in'}
          </button>
        </form>

        <div className="auth-card__divider">
          <span>Or</span>
        </div>

        <GoogleSignInButton onSuccess={handleGoogleSuccess} onError={handleGoogleError} />

        <p className="auth-card__footer">
          Don&rsquo;t have an account?{' '}
          <button
            type="button"
            className="auth-card__link"
            onClick={() => navigate('/signup', { state: stateFrom ? { from: stateFrom } : undefined })}
          >
            Sign up
          </button>
        </p>
      </div>
    </main>
  );
}
